import { useEffect, useRef, useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { AiImagePicker, type AiImage } from '@/components/ai-image-picker';

export interface TemplateData {
  id: string;
  name: string;
  subject: string;
  preheader: string | null;
  kind: string;
  currentVersion: number;
  mergeTags: string[];
  testSentAt: string | null;
  isExample: boolean;
  styleId: string | null;
  html: string;
  blocks: unknown | null;
  assets: string[];
  hasUnsubscribe: boolean;
}
type Version = { version: number; note: string; createdBy: string; createdAt: string; size: number };
type GrapesEditor = {
  setComponents: (html: string) => void;
  loadProjectData?: (data: unknown) => void;
  getProjectData: () => unknown;
  runCommand: (cmd: string) => string;
  getHtml: () => string;
  getCss: () => string;
  destroy: () => void;
  setDevice: (name: string) => void;
  AssetManager: { add: (src: string | string[]) => void };
  Panels: { getButton: (panel: string, id: string) => { set: (k: string, v: unknown) => void } | undefined };
  BlockManager: { add: (id: string, opts: Record<string, unknown>) => void };
};

const UNSUBSCRIBE_BLOCK = `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;margin:0 auto;"><tr><td style="padding:16px 24px 28px;text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:12px;line-height:1.6;color:#94a3b8;">You're receiving this because you have a Facet Flow account. <a href="{{unsubscribe_url}}" style="color:#94a3b8;text-decoration:underline;">Unsubscribe</a></td></tr></table>`;

export function TemplateEditor({ template, versions }: { template: TemplateData; versions: Version[] }) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const host = useRef<HTMLDivElement>(null);
  const editorRef = useRef<GrapesEditor | null>(null);
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const [html, setHtml] = useState(template.html);
  const [meta, setMeta] = useState({ name: template.name, subject: template.subject, preheader: template.preheader ?? '', kind: template.kind, isExample: template.isExample, styleId: template.styleId ?? '' });
  const [styles, setStyles] = useState<Array<{ id: string; name: string }>>([]);
  const [allTemplates, setAllTemplates] = useState<Array<{ id: string; name: string }>>([]);
  const [referenceId, setReferenceId] = useState('');
  useEffect(() => {
    api<Array<{ id: string; name: string }>>('/styles').then(setStyles).catch(() => setStyles([]));
    api<Array<{ id: string; name: string }>>('/templates').then((t) => setAllTemplates(t.filter((x) => x.id !== template.id))).catch(() => setAllTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [prompt, setPrompt] = useState('');
  const [promptImages, setPromptImages] = useState<AiImage[]>([]);
  const [hasUnsub, setHasUnsub] = useState(template.hasUnsubscribe);
  const [proposal, setProposal] = useState<{ html: string; warnings: string[] } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [editorReady, setEditorReady] = useState(false);

  // GrapesJS (client-only, dynamic import) with the newsletter preset.
  useEffect(() => {
    if (mode !== 'visual' || !host.current || editorRef.current) return;
    let cancelled = false;
    (async () => {
      const [{ default: grapesjs }, { default: newsletter }] = await Promise.all([import('grapesjs'), import('grapesjs-preset-newsletter')]);
      await import('grapesjs/dist/css/grapes.min.css');
      if (cancelled || !host.current) return;
      const brandInfo = await api<{ logoUrl: string | null; appUrl: string }>('/templates/brand').catch(() => ({ logoUrl: null, appUrl: 'https://facetflow.ai' }));
      const editor = grapesjs.init({
        container: host.current,
        height: 'calc(100vh - 240px)',
        fromElement: false,
        storageManager: false,
        plugins: [newsletter],
        pluginsOpts: { [newsletter as unknown as string]: {} },
        // Realistic device widths (the preset's buttons call these names). Mobile = iPhone-class 390px.
        deviceManager: {
          devices: [
            { id: 'desktop', name: 'Desktop', width: '' },
            { id: 'tablet', name: 'Tablet', width: '768px', widthMedia: '992px' },
            { id: 'mobile', name: 'Mobile portrait', width: '390px', widthMedia: '480px' },
          ],
        },
        assetManager: {
          assets: [...(brandInfo.logoUrl ? [brandInfo.logoUrl] : []), ...template.assets],
          uploadFile: async (e: DragEvent | Event) => {
            const files = ((e as DragEvent).dataTransfer?.files ?? (e.target as HTMLInputElement)?.files) as FileList | undefined;
            if (!files) return;
            for (const file of Array.from(files)) {
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
                reader.readAsDataURL(file);
              });
              const { url } = await api<{ url: string }>(`/templates/${template.id}/assets`, { method: 'POST', json: { filename: file.name, contentType: file.type || 'image/png', base64 } });
              editor.AssetManager.add(url);
            }
          },
        },
      }) as unknown as GrapesEditor;
      // Drag-in block so an unsubscribe line can be placed anywhere.
      if (brandInfo.logoUrl) {
        editor.BlockManager.add('ff-logo', {
          label: 'Facet Flow<br/>logo header',
          category: 'Facet Flow',
          content: `<table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td align="center" style="padding:24px 24px 8px;"><a href="${brandInfo.appUrl}" style="text-decoration:none;"><img src="${brandInfo.logoUrl}" alt="Facet Flow" width="120" style="display:block;width:120px;height:auto;border:0;" /></a></td></tr></table>`,
          attributes: { class: 'fa fa-diamond' },
        });
      }
      editor.BlockManager.add('ff-unsubscribe', { label: 'Unsubscribe<br/>footer', category: 'Facet Flow', content: UNSUBSCRIBE_BLOCK, attributes: { class: 'fa fa-envelope-o' } });
      if (template.blocks && editor.loadProjectData) editor.loadProjectData(template.blocks);
      else editor.setComponents(html);
      // Default to the phone view — that's how most recipients read email.
      editor.setDevice('Mobile portrait');
      editor.Panels.getButton('devices-c', 'set-device-mobile')?.set('active', true);
      editor.Panels.getButton('devices-c', 'set-device-desktop')?.set('active', false);
      editorRef.current = editor;
      setEditorReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  function currentHtml(): string {
    if (mode === 'visual' && editorRef.current) {
      const inlined = editorRef.current.runCommand('gjs-get-inlined-html');
      return typeof inlined === 'string' && inlined.trim() ? inlined : `<style>${editorRef.current.getCss()}</style>${editorRef.current.getHtml()}`;
    }
    return html;
  }

  async function run(name: string, fn: () => Promise<unknown>, okText: string) {
    setBusy(name);
    setMsg(null);
    try {
      await fn();
      setMsg({ kind: 'ok', text: okText });
    } catch (err) {
      setMsg({ kind: 'error', text: errMsg(err, 'Failed') });
    } finally {
      setBusy(null);
    }
  }

  async function saveDetails() {
    await api(`/templates/${template.id}`, { method: 'PATCH', json: { ...meta, styleId: meta.styleId || null } });
  }

  const saveVersion = (note: string, body?: string) =>
    run(
      'save',
      async () => {
        const out = body ?? currentHtml();
        await api(`/templates/${template.id}/versions`, { method: 'POST', json: { html: out, blocks: mode === 'visual' && editorRef.current ? editorRef.current.getProjectData() : undefined, note } });
        setHtml(out);
        revalidate();
      },
      'Saved as a new version',
    );

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl">{template.name}</h1>
            <p className="text-xs text-muted-foreground">
              v{template.currentVersion} · {template.kind} · tags: {template.mergeTags.join(', ') || 'none'}
              {!hasUnsub && (
                <span className="ml-2 inline-flex items-center gap-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-900">
                  no unsubscribe link — cannot be used for marketing
                  <button
                    type="button"
                    className="rounded bg-amber-900 px-2 py-0.5 text-white"
                    disabled={busy !== null}
                    onClick={() =>
                      run(
                        'unsub',
                        async () => {
                          const current = currentHtml();
                          if (current !== html) await api(`/templates/${template.id}/versions`, { method: 'POST', json: { html: current, note: 'autosave before adding unsubscribe' } });
                          const t = await api<{ html: string }>(`/templates/${template.id}/add-unsubscribe`, { method: 'POST' });
                          setHtml(t.html);
                          setHasUnsub(true);
                          if (mode === 'visual') editorRef.current?.setComponents(t.html);
                          revalidate();
                        },
                        'Unsubscribe footer added and saved as a new version',
                      )
                    }
                  >
                    Add unsubscribe footer
                  </button>
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button className={`rounded px-2 py-1 ${mode === 'visual' ? 'bg-muted' : ''}`} onClick={() => setMode('visual')}>
              Visual
            </button>
            <button
              className={`rounded px-2 py-1 ${mode === 'code' ? 'bg-muted' : ''}`}
              onClick={() => {
                if (mode === 'visual' && editorRef.current) setHtml(currentHtml());
                editorRef.current?.destroy();
                editorRef.current = null;
                setEditorReady(false);
                setMode('code');
              }}
            >
              HTML
            </button>
            <Button size="sm" onClick={() => saveVersion('manual edit')} disabled={busy !== null || (mode === 'visual' && !editorReady)}>
              Save version
            </Button>
          </div>
        </div>

        {mode === 'visual' ? (
          <div ref={host} className="min-h-[calc(100vh-240px)] w-full overflow-hidden rounded-lg border bg-white" />
        ) : (
          <textarea value={html} onChange={(e) => setHtml(e.target.value)} className="min-h-[calc(100vh-240px)] w-full rounded-lg border p-3 font-mono text-xs" />
        )}

        {proposal && (
          <div className="space-y-2 rounded-lg border bg-white p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base">Proposed change</h3>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => saveVersion(`prompt: ${prompt.slice(0, 120)}`, proposal.html).then(() => { setProposal(null); if (mode === 'visual') editorRef.current?.setComponents(proposal.html); else setHtml(proposal.html); })} disabled={busy !== null}>
                  Accept & save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setProposal(null)}>
                  Discard
                </Button>
              </div>
            </div>
            {proposal.warnings.length > 0 && <p className="rounded bg-amber-50 p-2 text-xs text-amber-900">{proposal.warnings.join(' · ')}</p>}
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Current</div>
                <iframe title="current" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={currentHtml()} className="h-96 w-full rounded border bg-white" />
              </div>
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Proposed</div>
                <iframe title="proposed" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={proposal.html} className="h-96 w-full rounded border bg-white" />
              </div>
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-2 rounded-lg border bg-white p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base">Rendered preview (sample contact, merge tags + links applied)</h3>
              <div className="flex items-center gap-2">
                <button className={`rounded px-2 py-1 text-xs ${previewDevice === 'desktop' ? 'bg-muted' : ''}`} onClick={() => setPreviewDevice('desktop')}>
                  Desktop
                </button>
                <button className={`rounded px-2 py-1 text-xs ${previewDevice === 'mobile' ? 'bg-muted' : ''}`} onClick={() => setPreviewDevice('mobile')}>
                  Mobile (390px)
                </button>
                <Button size="sm" variant="outline" onClick={() => setPreview(null)}>
                  Close
                </Button>
              </div>
            </div>
            <div className="flex justify-center bg-[var(--ff-surface-secondary)] p-3">
              <iframe title="preview" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={preview} className="h-[44rem] rounded border bg-white" style={{ width: previewDevice === 'mobile' ? 390 : '100%' }} />
            </div>
          </div>
        )}
      </div>

      <aside className="grid gap-4 text-sm md:grid-cols-2 xl:grid-cols-4">
        <section className="space-y-2 rounded-lg border bg-white p-4">
          <h3 className="text-base">Edit with a prompt</h3>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className="w-full rounded-md border p-2" placeholder='e.g. "Replace the hero with the attached image and make the CTA say Book a demo"' />
          <AiImagePicker images={promptImages} onChange={setPromptImages} disabled={busy !== null} />
          <Button
            size="sm"
            disabled={busy !== null || prompt.trim().length < 3}
            onClick={() =>
              run(
                'ai',
                async () => {
                  // Save what's on screen first so the model edits the latest HTML.
                  const current = currentHtml();
                  if (current !== html) {
                    await api(`/templates/${template.id}/versions`, { method: 'POST', json: { html: current, note: 'autosave before prompt edit' } });
                    setHtml(current);
                  }
                  const r = await api<{ html: string; warnings: string[]; model: string }>(`/templates/${template.id}/ai-edit`, {
                    method: 'POST',
                    json: { instruction: prompt, images: promptImages.map(({ filename, contentType, base64, note }) => ({ filename, contentType, base64, note: note || undefined })) },
                  });
                  setProposal(r);
                  setPromptImages([]);
                },
                'Proposal ready — review below',
              )
            }
          >
            {busy === 'ai' ? 'Thinking…' : 'Propose edit'}
          </Button>
          <p className="text-xs text-muted-foreground">Attached images are uploaded to Firebase Storage and handed to the model as URLs. Merge tags and the unsubscribe link are verified after the edit.</p>
        </section>

        <section className="space-y-2 rounded-lg border bg-white p-4">
          <h3 className="text-base">Restyle after a reference</h3>
          <p className="text-xs text-muted-foreground">Creates a new "[New] {template.name}" with this email's content unchanged, re-designed in the reference's style. This template stays as is.</p>
          <select value={referenceId} onChange={(e) => setReferenceId(e.target.value)} className="h-9 w-full rounded-md border px-2">
            <option value="">— pick a reference design —</option>
            {allTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={busy !== null || !referenceId}
            onClick={() =>
              run(
                'restyle',
                async () => {
                  const r = await api<{ id: string; warnings: string[] }>(`/templates/${template.id}/restyle`, { method: 'POST', json: { referenceTemplateId: referenceId } });
                  navigate(`/templates/${r.id}${r.warnings.length ? `?warnings=${encodeURIComponent(r.warnings.join(' · '))}` : ''}`);
                },
                'Restyled copy created',
              )
            }
          >
            {busy === 'restyle' ? 'Restyling… (30–60s)' : 'Create restyled copy'}
          </Button>
        </section>

        <section className="space-y-2 rounded-lg border bg-white p-4">
          <h3 className="text-base">Details</h3>
          <input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} className="h-9 w-full rounded-md border px-2" placeholder="Name" />
          <input aria-label="Subject" maxLength={200} disabled={busy !== null} value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} className="h-9 w-full rounded-md border px-2" placeholder="Subject" />
          <input aria-label="Preview text" maxLength={200} disabled={busy !== null} value={meta.preheader} onChange={(e) => setMeta({ ...meta, preheader: e.target.value })} className="h-9 w-full rounded-md border px-2" placeholder="Preheader" />
          <select value={meta.kind} onChange={(e) => setMeta({ ...meta, kind: e.target.value })} className="h-9 w-full rounded-md border px-2">
            <option value="newsletter">newsletter</option>
            <option value="flow">flow</option>
            <option value="transactional">transactional</option>
          </select>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Style</span>
            <select value={meta.styleId} onChange={(e) => setMeta({ ...meta, styleId: e.target.value })} className="h-9 w-full rounded-md border px-2">
              <option value="">— no style —</option>
              {styles.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-start gap-2 rounded-md border p-2 text-xs">
            <input type="checkbox" checked={meta.isExample} onChange={(e) => setMeta({ ...meta, isExample: e.target.checked })} className="mt-0.5" />
            <span>
              <b>Use as an AI example for its style</b>
              <br />
              <span className="text-muted-foreground">Shown to the model when generating templates in this style (newest 3 examples per style).</span>
            </span>
          </label>
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run('meta', () => saveDetails().then(() => revalidate()), 'Details saved')}>
            Save details
          </Button>
        </section>

        <section className="space-y-2 rounded-lg border bg-white p-4">
          <h3 className="text-base">Preview & test</h3>
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run('preview', () => saveDetails().then(() => api<{ html: string }>(`/templates/${template.id}/preview`, { method: 'POST', json: {} })).then((r) => { revalidate(); setPreviewDevice('desktop'); setPreview(r.html); setTimeout(() => document.querySelector('iframe[title="preview"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }), 'Preview rendered')}>
            Render preview
          </Button>
          <div className="flex gap-2">
            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-9 flex-1 rounded-md border px-2" placeholder="you@facetflow.io" />
            <Button
              size="sm"
              disabled={busy !== null || !testTo.trim()}
              onClick={() => {
                if (!confirm(`Send a test of "${meta.subject}" to ${testTo}?\n(In sandbox mode it is redirected to SANDBOX_RECIPIENT.)`)) return;
                run('test', () => saveDetails().then(() => api<{ sendMode: string; sandbox?: boolean; to: string }>(`/templates/${template.id}/test-send`, { method: 'POST', json: { to: testTo } })).then((r) => { setMsg({ kind: 'ok', text: r.sandbox ? `Sent — sandbox mode redirected it to SANDBOX_RECIPIENT (requested ${r.to})` : `Sent to ${r.to}` }); revalidate(); }), 'Test email sent');
              }}
            >
              Test send
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Preview and test save your current details first and use the latest saved email body. Preview text appears in the inbox, not the email body.</p>
          <p className="text-xs text-muted-foreground">Last test: {template.testSentAt ? fmtDateTime(template.testSentAt) : 'never'}</p>
        </section>

        <section className="space-y-2 rounded-lg border bg-white p-4">
          <h3 className="text-base">Versions</h3>
          <ul className="space-y-1 text-xs">
            {versions.map((v) => (
              <li key={v.version} className="flex items-center justify-between gap-2">
                <span>
                  <b>v{v.version}</b> {v.note} · {v.createdBy} · {fmtDateTime(v.createdAt)}
                </span>
                {v.version !== template.currentVersion && (
                  <button
                    className="ff-link"
                    onClick={() =>
                      run('restore', () => api<{ html: string }>(`/templates/${template.id}?version=${v.version}`).then((t) => saveVersion(`restored v${v.version}`, t.html)), `Restored v${v.version}`)
                    }
                  >
                    restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-2 rounded-lg border bg-white p-4">
          <h3 className="text-base">Danger zone</h3>
          <Button size="sm" variant="destructive" disabled={busy !== null} onClick={() => { if (confirm('Delete this template and all versions?')) run('delete', () => api(`/templates/${template.id}`, { method: 'DELETE' }).then(() => navigate('/templates')), 'Deleted'); }}>
            Delete template
          </Button>
        </section>

        {msg && (
          <p role="status" className={`rounded-md p-2 text-xs md:col-span-2 xl:col-span-4 ${msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
            {msg.text}
          </p>
        )}
      </aside>
    </div>
  );
}
